import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/delivery.scss";
import "../css/privacy.scss";

const privacyHighlights = [
  { label: "Our approach", value: "Collect only what we need" },
  { label: "Your choices", value: "Access, correct or delete" },
  { label: "Questions", value: siteConfig.contact.customerServiceEmail },
];

const privacySections = [
  {
    id: "scope",
    title: "Scope of this policy",
    body: [
      "This policy explains how " +
        siteConfig.brandName +
        " handles personal information when you browse our store, place an order, contact us or otherwise interact with our online services.",
      "It does not cover third-party websites or services reached through external links. Those providers publish and apply their own privacy terms.",
    ],
  },
  {
    id: "collection",
    title: "Information we collect",
    body: [
      "We may receive information you provide directly, such as your name, billing and delivery details, email, account preferences, order history and the content of customer-care messages.",
      "When you use the site, standard technical information may also be generated, including device and browser details, approximate location, pages viewed, referral source, cookie identifiers and interactions with the store.",
    ],
    bullets: [
      "Payment providers process full payment credentials; we generally receive confirmation and limited transaction details.",
      "We do not intentionally request sensitive personal information through the store or contact form.",
      "If you provide information about another person, you should have their permission to do so.",
    ],
  },
  {
    id: "use",
    title: "How we use information",
    body: [
      "We use information to operate the store, fulfil and deliver orders, take payment, provide support, prevent misuse, maintain security and meet record-keeping obligations.",
      "Where permitted, we also use limited information to understand store performance and improve products, support and store communications.",
    ],
  },
  {
    id: "basis",
    title: "Why processing is permitted",
    body: [
      "Depending on where you live and the context, we process information because it is needed to perform a contract with you, comply with a legal duty, pursue a legitimate business interest that does not override your rights, or because you have given consent.",
      "When processing relies on consent, you may withdraw that consent for future use at any time.",
    ],
  },
  {
    id: "sharing",
    title: "When information is shared",
    body: [
      "We share only what is reasonably necessary with service providers that help run the store, such as hosting, payment, fraud prevention, fulfilment, delivery, analytics and customer support.",
      "Information may also be disclosed where the law requires it, to protect customers or the store, or as part of a genuine business reorganisation. We do not sell personal information for money.",
    ],
  },
  {
    id: "cookies",
    title: "Cookies & similar tools",
    body: [
      "The site may use essential cookies to remember your bag, keep checkout secure and provide core features. With your choice where required, optional tools may measure visits, remember preferences or help us understand campaign performance.",
      "Browser controls can remove or block cookies, but disabling essential cookies may stop parts of the store from working correctly.",
    ],
  },
  {
    id: "retention",
    title: "Retention & protection",
    body: [
      "We keep information only for as long as it serves the purpose described here, supports warranties or disputes, or is required for tax, accounting and other legal records. Retention periods therefore vary by data type.",
      "We use organisational and technical safeguards appropriate to the nature of the information. No online service can promise absolute security, so please use a unique password and tell us if you suspect unauthorised activity.",
    ],
  },
  {
    id: "rights",
    title: "Your privacy choices",
    body: [
      "Depending on your location, you may ask to access, correct, delete, restrict or receive a portable copy of personal information, or object to certain uses. You may also have the right to complain to your local data-protection authority.",
      "We may need to verify your identity before completing a request and may retain limited information when the law allows or requires it. Authorised agents should provide evidence that they may act for you.",
    ],
    contact: true,
  },
  {
    id: "updates",
    title: "Children, updates & contact",
    body: [
      "Our store is intended for adults and is not knowingly directed to children. If you believe a child has provided personal information without appropriate permission, please contact us.",
      "We may revise this policy when our practices, services or legal responsibilities change. The latest version will appear on this page with an updated date.",
    ],
    contact: true,
  },
];

function Privacy() {
  return (
    <main className="privacy policy-page" id="main-content">
      <header className="policy-page__header">
        <p className="policy-page__eyebrow">Legal</p>
        <h1>Privacy</h1>
        <p>
          A plain-language overview of the information our store uses and the
          choices available to you.
        </p>
        <p className="policy-page__updated">
          Last updated <time dateTime="2026-09-30">30 September 2026</time>
        </p>
      </header>

      <dl className="policy-page__summary" aria-label="Privacy at a glance">
        {privacyHighlights.map((item) => (
          <div key={item.label}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
      </dl>

      <div className="policy-page__layout">
        <aside className="policy-page__aside" aria-label="On this page">
          <p>On this page</p>
          <ol>
            {privacySections.map((section) => (
              <li key={section.id}>
                <a href={"#" + section.id}>{section.title}</a>
              </li>
            ))}
          </ol>
          <div className="policy-page__help">
            <span>Privacy request?</span>
            <Link to="/contact">Contact customer care</Link>
          </div>
        </aside>

        <article className="policy-page__content">
          {privacySections.map((section, index) => (
            <section id={section.id} key={section.id}>
              <span className="policy-page__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h2>{section.title}</h2>
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {section.bullets && (
                  <ul>
                    {section.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                )}
                {section.contact && (
                  <p>
                    Send requests or questions to{" "}
                    <a
                      href={
                        "mailto:" + siteConfig.contact.customerServiceEmail
                      }
                    >
                      {siteConfig.contact.customerServiceEmail}
                    </a>
                    .
                  </p>
                )}
              </div>
            </section>
          ))}
        </article>
      </div>
    </main>
  );
}

export default Privacy;
